import { defineComponent, defineTag } from '@/core/ecs/ComponentStore'
import type { Entity } from '@/core/ecs/Entity'
import { NULL_ENTITY } from '@/core/ecs/Entity'
import { Rng } from '@/core/Rng'

// ---------------------------------------------------------------- identity

/**
 * Slot index of a fighter, 0-based. Not limited to two: a match is a free-for-all
 * of up to `MAX_FIGHTERS`, of which at most two can be human (one keyboard, one
 * gamepad) and the rest are bots.
 */
export type PlayerId = number

export const MAX_FIGHTERS = 6
export const MAX_HUMANS = 2

export const AbilityId = {
  Push: 0,
  Dash: 1,
  Wave: 2,
  Brace: 3,
  Vortex: 4,
} as const
export type AbilityId = (typeof AbilityId)[keyof typeof AbilityId]
export const ABILITY_COUNT = 5

// --------------------------------------------------------------- transform

/**
 * Position on the XZ plane plus visual height. `prev*` fields hold the state at
 * the start of the current fixed step so the render stage can interpolate — the
 * simulation itself never reads them.
 */
export const Transform = defineComponent('Transform', () => ({
  x: 0,
  z: 0,
  y: 0,
  angle: 0,
  prevX: 0,
  prevZ: 0,
  prevY: 0,
  prevAngle: 0,
}))

export const Velocity = defineComponent('Velocity', () => ({
  x: 0,
  z: 0,
  y: 0,
}))

// ---------------------------------------------------------------- physics

export type ColliderShape = 'circle' | 'box'

/**
 * Circle bodies for everything that moves, boxes for large static cover
 * (car wrecks, concrete blocks). Box colliders are axis-aligned in local space
 * and rotated by `angle` — collision resolves in the box's local frame.
 */
export const Collider = defineComponent('Collider', () => ({
  shape: 'circle' as ColliderShape,
  radius: 0.5,
  halfX: 0.5,
  halfZ: 0.5,
  /** Rotation of a box collider, radians. Ignored for circles. */
  angle: 0,
}))

export const Body = defineComponent('Body', () => ({
  mass: 1,
  invMass: 1,
  /** Bounciness of body-vs-body impacts, 0..1. */
  restitution: 0.4,
  /** Velocity decay applied while no thrust is present, 1/s. */
  drag: 3.2,
  /** Immovable: infinite mass, never integrated. */
  isStatic: false,
  /** Soft barriers absorb a fraction of the incoming impulse instead of stopping it. */
  absorb: 0,
}))

/** Everything the collision system may push around. */
export const Dynamic = defineTag('Dynamic')

/** Static world geometry: trees, wrecks, pillars, barriers. */
export const Static = defineTag('Static')

// ----------------------------------------------------------------- actors

/**
 * A controllable character. `thrust` is the normalised input direction; the
 * movement system converts it into acceleration and caps only the component of
 * velocity along the thrust axis — external knockback is deliberately uncapped.
 */
export const Actor = defineComponent('Actor', () => ({
  player: 0 as PlayerId,
  thrustX: 0,
  thrustZ: 0,
  maxSpeed: 7,
  accel: 45,
  /**
   * The unbuffed values, so timed effects can scale them and restore them
   * without hard-coding the defaults somewhere else and quietly reverting any
   * future per-character or per-arena tuning.
   */
  baseSpeed: 7,
  baseAccel: 45,
  /** Ground friction applied when there is no input, 1/s. */
  friction: 8,
  /** 0..1 multiplier on input authority — drops while being knocked around. */
  control: 1,
  /** Distance walked, drives the procedural step animation. */
  gait: 0,
}))

/** Instability replaces health: it scales the knockback the actor receives. */
export const Instability = defineComponent('Instability', () => ({
  value: 0,
  max: 100,
  /** Seconds since the last hit; decay only starts after a grace period. */
  sinceHit: 99,
}))

/**
 * Cooldowns are modelled uniformly as charges: a one-shot ability is simply an
 * ability with one charge. `recharge` counts down toward restoring the next
 * charge, which is exactly what the HUD arc draws.
 */
export const Abilities = defineComponent('Abilities', () => ({
  charges: new Int8Array(ABILITY_COUNT),
  maxCharges: new Int8Array(ABILITY_COUNT),
  recharge: new Float32Array(ABILITY_COUNT),
  /**
   * Cooldown per ability, in seconds — **per fighter**, not per ability.
   * Each character runs the same five buttons at their own tempo; the shared
   * table in `definitions.ts` is the baseline and the kit scales it.
   */
  cooldown: new Float32Array(ABILITY_COUNT),
  /** Input buffer per ability, seconds remaining; lets presses survive lockout. */
  buffer: new Float32Array(ABILITY_COUNT),
}))

/**
 * A fighter's kit: how their version of the five shared abilities differs.
 *
 * Multipliers rather than absolute numbers, deliberately. The balance table in
 * `definitions.ts` stays the single source of what a Push *is*; a kit says only
 * how this character bends it. That keeps a global retune working on everyone,
 * and it makes "is anybody overtuned" a question about six short rows instead
 * of six full tables.
 */
export const Kit = defineComponent('Kit', () => ({
  pushPower: 1,
  boltSpeed: 1,
  wavePower: 1,
  waveRadius: 1,
  dashPower: 1,
  vortexPower: 1,
  vortexRadius: 1,
  /** Multiplier on the knockback Brace soaks; below 1 is a better stance. */
  braceMul: 1,
  braceHold: 1,
}))

export type CastPhase = 'windup' | 'active' | 'recover'

/** Present only while an ability is mid-animation. */
export const Casting = defineComponent('Casting', () => ({
  ability: AbilityId.Push as number,
  phase: 'windup' as CastPhase,
  timer: 0,
}))

export const Dashing = defineComponent('Dashing', () => ({
  timeLeft: 0,
  dirX: 0,
  dirZ: 1,
  speed: 16,
  /** Entities already body-checked by this dash. */
  hits: [] as Entity[],
}))

export const Bracing = defineComponent('Bracing', () => ({
  timeLeft: 0,
  /** Time the stance has been up, so a released one can serve its minimum. */
  elapsed: 0,
}))

export const Stunned = defineComponent('Stunned', () => ({
  timeLeft: 0,
}))

/**
 * Hitstun: the brief window after a solid hit during which the victim has no
 * say in where they are going.
 *
 * This is what makes a push read as a push. Without it a knockback is just a
 * velocity change the victim immediately starts steering out of, and the whole
 * exchange feels mushy — the hit lands but nothing *happens*. With it there is
 * a moment where you are visibly launched and can only watch, and the impact
 * has a beginning, a middle and an end.
 */
/**
 * A knockback in flight. See docs/PHYSICS.md §6.2.
 *
 * The old model added an impulse to velocity and let exponential drag bleed it
 * off. Its fixed point was walking speed, so a hit never actually ended — it
 * dissolved into ordinary movement over seconds (measured: 7.0000000000000089
 * m/s and 424 m at t = 60 s), and its final outcome was decided by whether the
 * victim happened to hold a stick direction, an 11× spread on identical hits.
 *
 * This is a launch instead: full speed for `hold` seconds, then constant
 * deceleration to a stop. Duration scales with the impulse and distance with
 * its square, so a big hit is visibly *longer* as well as further — which is
 * what reads as "harder". The victim keeps a bounded say (`di`), not the
 * outcome.
 */
export const Launch = defineComponent('Launch', () => ({
  /** In the air between bounces; false once settled and sliding to a stop. */
  airborne: true,
  /** Ground contacts so far — the visible measure of how hard the hit was. */
  bounces: 0,
  /** Expected total flight time, for the visuals and the HUD. */
  duration: 0,
  elapsed: 0,
  /** Launch speed at the moment of impact, m/s. */
  speed: 0,
  /** Trajectory rotation the victim has already spent, radians. */
  di: 0,
}))

export const Hitstun = defineComponent('Hitstun', () => ({
  timeLeft: 0,
  /** Total duration, so the visuals can show how much of it is left. */
  duration: 0,
}))

/** Attached when an actor leaves the platform; simulation of the fall only. */
export const Falling = defineComponent('Falling', () => ({
  timer: 0,
  spin: 0,
  reported: false,
}))

/** Suppresses all input and ability activation (countdown, round end). */
export const InputLocked = defineTag('InputLocked')

// ------------------------------------------------------------------ world

export type PropKind =
  | 'crate'
  | 'barrel'
  | 'tree'
  | 'car'
  | 'pillar'
  | 'barrier'
  | 'block'

export const Prop = defineComponent('Prop', () => ({
  kind: 'crate' as PropKind,
  /** Cosmetic seed so identical props do not look identical. */
  seed: 0,
}))

export const Explosive = defineComponent('Explosive', () => ({
  /** Impact speed that sets it off, m/s. */
  threshold: 12,
  radius: 4,
  power: 14,
  instability: 10,
  armed: true,
  fuse: 0,
}))

export type HazardKind =
  /** Fires a launching burst on a timer, with a loud telegraph. */
  | 'burner'
  /** Pulls constantly, never moves. */
  | 'vortex'
  /** A wandering pull that also spins its victims around it. */
  | 'tornado'
  /** Wide, frequent discharge: weak push, heavy instability. */
  | 'electro'

/** Static arena hazards placed by the level, distinct from the Vortex ability. */
export const Hazard = defineComponent('Hazard', () => ({
  kind: 'burner' as HazardKind,
  radius: 3,
  power: 12,
  instability: 8,
  /** Seconds between bursts (burner only). */
  interval: 3.5,
  /** Warning duration before a burst (burner only). */
  telegraph: 0.8,
  timer: 0,
  /** Continuous pull acceleration (vortex/tornado), m/s². */
  pull: 4,
  /** Sideways swirl added on top of the pull (tornado only), m/s². */
  swirl: 0,
  /** Drift velocity of a wandering hazard. */
  driftX: 0,
  driftZ: 0,
  /** `timer` value at the last warning ring, so it is paced, not per-frame. */
  lastWarn: 0,
  /** Decays from 1 after a burst; drives the flare in the visual. */
  flash: 0,
}))

// --------------------------------------------------------------------- ai

export type Difficulty = 0 | 1 | 2

/**
 * Marks a fighter as computer-controlled. The AI writes into the shared input
 * frame rather than into the actor, so everything downstream treats it exactly
 * like a player holding a gamepad.
 */
export const AiController = defineComponent('AiController', () => ({
  difficulty: 1 as Difficulty,
  /**
   * The bot's own randomness.
   *
   * It must not draw from the simulation's RNG: the AI thinks once per rendered
   * frame, so on a 144 Hz display it would consume a different number of draws
   * before each fixed step than on a 60 Hz one, and the tornado paths and pickup
   * spawns would diverge from an identical seed and identical inputs.
   */
  rng: new Rng(0x5ca1ab1e),
  /** Counts down to the next re-plan; higher difficulty thinks more often. */
  decisionTimer: 0,
  /** Smoothed movement intent, so the bot does not twitch every frame. */
  moveX: 0,
  moveZ: 0,
  /** Persistent aim error, re-rolled on each decision. */
  aimError: 0,
  /** Cooldown before the bot may press an ability again, in seconds. */
  actionTimer: 0,
  /**
   * How much longer the bot intends to hold the stance. A hold has to be
   * re-asserted every frame, but the bot only re-plans a few times a second,
   * so the intent is stored as a countdown rather than a flag.
   */
  braceTimer: 0,
}))

// ------------------------------------------------------------- appearance

export type CharacterKind = 'guard' | 'beetle' | 'crane' | 'brick' | 'jester' | 'blob'

// ---------------------------------------------------------------- pickups

export type PickupKind = 'shield' | 'haste'

/** A collectable that grants a timed buff to whoever walks into it. */
export const Pickup = defineComponent('Pickup', () => ({
  kind: 'shield' as PickupKind,
  radius: 0.9,
  /** Seconds since spawn, drives the bob and spin of the visual. */
  age: 0,
}))

/**
 * Timed effects on an actor. Both are deliberately loud and short: they should
 * change one exchange decisively and then be gone.
 */
export const Buff = defineComponent('Buff', () => ({
  /** Immunity to all knockback while above zero. */
  shield: 0,
  /** Movement bonus while above zero. */
  haste: 0,
}))

/** The Vortex ability's projectile-turned-anomaly. */
export const VortexField = defineComponent('VortexField', () => ({
  owner: NULL_ENTITY as Entity,
  player: 0 as PlayerId,
  phase: 'arming' as 'arming' | 'pulling' | 'burst',
  timer: 0,
  radius: 4.5,
  pull: 8,
  burstPower: 13,
  instability: 12,
}))

/**
 * A thrown bolt of plasma — the Push ability. See docs/PHYSICS.md §6.2а.
 *
 * Push used to be a melee cone, which made it the same verb as Wave (a radial
 * impulse centred on you) and left obstacles as furniture. As a projectile it
 * flies where you are facing, skips off the slab and reflects off obstacles, so
 * facing finally decides something, cover becomes real, and a ricochet is a
 * thing you can aim on purpose.
 */
export const Projectile = defineComponent('Projectile', () => ({
  owner: NULL_ENTITY as Entity,
  player: 0 as PlayerId,
  radius: 0.45,
  /** Half-width of the crescent's front; the hit test is a swept rectangle. */
  halfWidth: 1,
  /** Seconds before it may collide with its own caster. */
  selfGrace: 0.15,
  life: 2.2,
  bounces: 0,
}))

/** An expanding ring of force (Wave ability, explosions). */
export const Shockwave = defineComponent('Shockwave', () => ({
  owner: NULL_ENTITY as Entity,
  x: 0,
  z: 0,
  radius: 0,
  maxRadius: 6.5,
  speed: 18,
  /** Impulse at the epicentre; falls off linearly to `powerEdge`. */
  power: 9,
  powerEdge: 4,
  instability: 5,
  /** Ignores the caster so a wave never self-pushes. */
  hits: [] as Entity[],
}))

export const Lifetime = defineComponent('Lifetime', () => ({
  remaining: 1,
}))

// ----------------------------------------------------------------- render

/**
 * Bridge to the Three.js scene. `objectId` indexes the renderer's object
 * registry; nothing in the simulation ever touches a mesh directly.
 */
export const Renderable = defineComponent('Renderable', () => ({
  objectId: -1,
  /** Vertical offset applied on top of Transform.y. */
  yOffset: 0,
  visible: true,
}))

/** Marks the two player characters for quick lookup. */
export const PlayerTag = defineComponent('PlayerTag', () => ({
  player: 0 as PlayerId,
  /** Team colour as 0xRRGGBB, used by VFX and HUD. */
  color: 0xffffff,
}))
